import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AccountInfo, type EventMessage } from '@azure/msal-browser';
import { App } from './App';
import { msalInstance, ensureMsalInitialized } from './auth/index';
import './index.css';

// Per spec §17 Performance: React Query for cache + retries on the analytics API path.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Analytics views are snapshot-pinned; no need to refetch on focus.
      refetchOnWindowFocus: false,
      // Per spec §19 PHI cache discipline: don't keep stale data around longer than needed.
      staleTime: 30_000,
      gcTime: 60_000,
      retry: (failureCount, error) => {
        // Don't retry 4xx — they indicate auth/RBAC issues per §19.
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

// Initialize MSAL AND process any pending auth redirect before rendering
// (Diamond Spec Rev4 D1/D8). Awaiting handleRedirectPromise() pre-render is
// what closes the readiness race: the account cache is populated before any
// component can fire a gated /api call, so getApiToken() never misses the
// signed-in account on the post-Entra return navigation. Token WARMING stays
// post-render (App.tsx) per D8 - never block render on acquireTokenSilent.
ensureMsalInitialized()
  .then(() => msalInstance.handleRedirectPromise())
  .catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('MSAL init/redirect processing failed (login wall will catch unauthenticated state)', e);
  })
  .finally(() => {
    // Pin the active account so getApiToken() mints the bearer for the SIGNED-IN
    // identity rather than getAllAccounts()[0] (which can be a foreign / second
    // cached account whose email/tid fail the server's second-factor match).
    // msal-react's MsalProvider runs handleRedirectPromise but does NOT auto-set
    // the active account in msal-browser v3, so we set it here and keep it synced.
    // Harmless when the bearer flag is off (getApiToken returns null regardless).
    try {
      if (!msalInstance.getActiveAccount()) {
        const first = msalInstance.getAllAccounts()[0];
        if (first) msalInstance.setActiveAccount(first);
      }
      msalInstance.addEventCallback((event: EventMessage) => {
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          event.payload && 'account' in event.payload && event.payload.account
        ) {
          msalInstance.setActiveAccount(event.payload.account as AccountInfo);
        }
      });
    } catch (e) {
      // non-fatal: token acquisition still falls back to getAllAccounts()[0]
      // eslint-disable-next-line no-console
      console.warn('MSAL active-account wiring failed', e);
    }
    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </MsalProvider>
      </StrictMode>,
    );
  });
